import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip, Divider, useTheme } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

interface RichTextEditorProps {
  label?: string;
  value: string;
  onChange?: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  maxHeight?: number | string;
  maxLength?: number;
  showCharCounter?: boolean;
}

const exec = (command: string) => {
  document.execCommand(command, false);
};

const stripHtml = (html: string) => {
  const temp = document.createElement('div');
  temp.innerHTML = html || '';
  return (temp.textContent || temp.innerText || '').trim();
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  placeholder,
  required,
  maxHeight = '60vh',
  maxLength,
  showCharCounter = true
}) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  return (
    <Box sx={{ width: '100%', display: 'grid' }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          {label}{required ? ' *' : ''}
        </Typography>
      )}

      <Box sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        width: '100%',
        display: 'grid',
        maxWidth: '100%',
        backgroundColor: theme.palette.background.paper
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
          <Tooltip title="Negrito">
            <span>
              <IconButton size="small" onClick={() => exec('bold')} disabled={disabled}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Itálico">
            <span>
              <IconButton size="small" onClick={() => exec('italic')} disabled={disabled}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sublinhado">
            <span>
              <IconButton size="small" onClick={() => exec('underline')} disabled={disabled}>
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <Tooltip title="Lista com marcadores">
            <span>
              <IconButton size="small" onClick={() => exec('insertUnorderedList')} disabled={disabled}>
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <span>
              <IconButton size="small" onClick={() => exec('insertOrderedList')} disabled={disabled}>
                <FormatListNumberedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Divider />

        <Box
          ref={editorRef}
          component="div"
          contentEditable={!disabled}
          role="textbox"
          aria-multiline
          suppressContentEditableWarning
          onInput={(e) => {
            if (!onChange) return;
            const html = (e.target as HTMLDivElement).innerHTML;
            const textContent = stripHtml(html);

            // Verificar limite de caracteres se especificado
            if (maxLength && textContent.length > maxLength) {
              return; // Não permitir entrada além do limite
            }

            onChange(html);
          }}
          sx={{
            width: '100%',
            maxWidth: '100%',
            minHeight: 150,
            maxHeight: maxHeight,
            p: 2,
            overflowY: 'auto',
            overflowX: 'auto',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            color: theme.palette.text.primary,
            backgroundColor: 'transparent',
            '&:empty:before': {
              content: `'${placeholder || ''}'`,
              color: theme.palette.text.disabled
            },
            '&:focus': {
              outline: 'none'
            }
          }}
        />
      </Box>

      {showCharCounter && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: 'block',
            color: maxLength && stripHtml(value).length > maxLength ? 'error.main' : 'text.disabled'
          }}
        >
          {stripHtml(value).length}{maxLength ? `/${maxLength}` : ''} caracteres
          {maxLength && stripHtml(value).length > maxLength && ' (limite excedido)'}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;


